/*
  Size guide fallback desde Excel (Shopify > Contenido > Archivos)

  Se activa SOLO en productos que NO tengan asignado el metaobject
  product.metafields.laagam.size_guide. Los productos que ya lo tienen
  siguen renderizando con Liquid como hasta ahora.

  Workflow:
    1) Sube tu Excel a Shopify > Contenido > Archivos
    2) Copia la URL CDN que te da Shopify (boton "Copiar enlace")
    3) Pegala en: Personalizar tema > Configuracion del tema > Size guide
    4) Para actualizar medidas: edita el Excel y *reemplaza* el archivo
       en Shopify (la URL se mantiene si reemplazas, no si subes uno nuevo)

  Formato esperado del Excel:
    Bloques separados por filas en blanco. Cada bloque:
      Fila A: nombre del modelo (informativo, no se usa)
      Fila B: SKU base (ej: TP/M2009 o AFW2301)            <- obligatorio
      [Fila opcional]: IMAGE | https://cdn.shopify.com/.../foto.png
      Fila C: MEDIDAS | TALLA XS | TALLA S | TALLA M | TALLA L | TALLA XL
      Fila D+: nombre de medida | 37CM | 38CM | ... (admite "37,5CM")

  La fila IMAGE es opcional y puede ir en cualquier sitio del bloque.
  Si esta presente, aparece el bloque "How to measure" con la imagen
  + la lista numerada de medidas. Si no, solo la tabla.
*/
(function () {
  'use strict';

  const XLSX_URL = (window.SGD_XLSX_URL || '').trim();
  const SHEETJS_CDN = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';

  let dataPromise = null;

  function loadSheetJS() {
    if (window.XLSX) return Promise.resolve(window.XLSX);
    return new Promise(function (resolve, reject) {
      const s = document.createElement('script');
      s.src = SHEETJS_CDN;
      s.onload = function () { resolve(window.XLSX); };
      s.onerror = function () { reject(new Error('Failed to load SheetJS')); };
      document.head.appendChild(s);
    });
  }

  function loadGuides() {
    if (dataPromise) return dataPromise;
    if (!XLSX_URL) {
      console.warn('[size-guide-from-xlsx] URL no configurada. Pegala en Personalizar tema > Configuracion del tema > Size guide.');
      return Promise.resolve(null);
    }
    dataPromise = (async function () {
      const XLSX = await loadSheetJS();
      const res = await fetch(XLSX_URL);
      if (!res.ok) throw new Error('xlsx fetch failed: ' + res.status);
      const buf = await res.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      return parseGuides(rows);
    })();
    return dataPromise;
  }

  const GARMENT_IMG_RE = /^(GARMENT\s+)?(IMAGEN|IMAGE|IMG)$/i;
  const BODY_IMG_RE = /^BODY\s+(IMAGEN|IMAGE|IMG)$/i;
  const ANY_IMG_RE = /^(GARMENT\s+|BODY\s+)?(IMAGEN|IMAGE|IMG)$/i;
  const HEADER_LABEL_RE = /^(MEDIDAS|MEASUREMENTS|MEASURES|SIZES?)$/i;

  function sgdStr(v) { return String(v == null ? '' : v).trim(); }
  function sgdClean(raw) { return sgdStr(raw).replace(/\s*CM\s*$/i, '').replace(',', '.').trim(); }
  // Lee cabeceras de talla desde `start` hasta la primera celda vacía (así la
  // tabla de prenda no se traga la cabecera de la tabla de cuerpo que va al lado).
  function sgdReadSizes(row, start) {
    const out = [];
    for (let c = start; c < row.length; c++) { const h = sgdStr(row[c]); if (!h) break; out.push(h); }
    return out;
  }

  function parseGuides(rows) {
    const guides = {};
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i] || [];
      if (!HEADER_LABEL_RE.test(sgdStr(r[0]))) continue; // cabecera de PRENDA en la columna A

      // SKU + imágenes (garment/body) en las filas por encima de la cabecera
      let skuRow = -1, garmentImg = null, bodyImg = null;
      for (let j = i - 1; j >= Math.max(0, i - 6); j--) {
        const rowj = rows[j] || [];
        let hadImg = false;
        for (let c = 0; c < rowj.length; c++) {
          const v = sgdStr(rowj[c]); if (!v) continue;
          if (BODY_IMG_RE.test(v)) { bodyImg = sgdStr(rowj[c + 1]) || bodyImg; hadImg = true; }
          else if (GARMENT_IMG_RE.test(v)) { garmentImg = sgdStr(rowj[c + 1]) || garmentImg; hadImg = true; }
        }
        if (hadImg) continue;
        if (sgdStr(rowj[0])) { skuRow = j; break; }
      }
      if (skuRow < 0) continue;

      const sku = sgdStr((rows[skuRow] || [])[0]).toUpperCase();
      let name = '';
      for (let j = skuRow - 1; j >= Math.max(0, skuRow - 4); j--) {
        const v = sgdStr((rows[j] || [])[0]); if (!v) continue;
        if (ANY_IMG_RE.test(v)) continue;
        name = v; break;
      }

      // Tallas de prenda: col 1 hasta la primera vacía
      const sizes = sgdReadSizes(r, 1);
      if (!sizes.length) continue;

      // ¿Hay una segunda cabecera (tabla de CUERPO) a la derecha? La localizamos
      // por la etiqueta, no por una columna fija (robusto a distinto nº de tallas).
      let bodyCol = -1;
      for (let c = 1 + sizes.length; c < r.length; c++) {
        if (HEADER_LABEL_RE.test(sgdStr(r[c]))) { bodyCol = c; break; }
      }
      const bodySizes = bodyCol > 0 ? sgdReadSizes(r, bodyCol + 1) : [];

      const measurements = [], bodyMeas = [];
      for (let j = i + 1; j < rows.length; j++) {
        const mr = rows[j] || [];
        const label = sgdStr(mr[0]);
        if (!label) break; // fila en blanco = fin del bloque de este SKU
        if (label.indexOf('**') !== 0 && !ANY_IMG_RE.test(label)) {
          const values = [];
          for (let c = 1; c <= sizes.length; c++) values.push(sgdClean(mr[c]));
          measurements.push({ name: label, values: values, description: sgdStr(mr[sizes.length + 1]) });
        }
        if (bodyCol > 0) {
          const blabel = sgdStr(mr[bodyCol]);
          if (blabel && blabel.indexOf('**') !== 0 && !ANY_IMG_RE.test(blabel)) {
            const bvals = [];
            for (let c = bodyCol + 1; c <= bodyCol + bodySizes.length; c++) bvals.push(sgdClean(mr[c]));
            bodyMeas.push({ name: blabel, values: bvals, description: sgdStr(mr[bodyCol + bodySizes.length + 1]) });
          }
        }
      }
      if (measurements.length) {
        guides[sku] = {
          name: name, sizes: sizes, measurements: measurements, imageUrl: garmentImg,
          body: (bodyCol > 0 && bodyMeas.length) ? { sizes: bodySizes, measurements: bodyMeas, imageUrl: bodyImg } : null
        };
      }
    }
    return guides;
  }

  function findGuide(productSku, guides) {
    if (!productSku) return null;
    const sku = String(productSku).toUpperCase().trim();
    const keys = Object.keys(guides).sort(function (a, b) { return b.length - a.length; });
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      // Separadores de variante admitidos: laagam usa "BASE/COLOR", y niihai
      // "BASE_TALLA" (p. ej. OLD_TPS_BASV_BLU_XS), de ahi el guion bajo.
      if (sku === k || sku.indexOf(k + '/') === 0 || sku.indexOf(k + '-') === 0 || sku.indexOf(k + '_') === 0) {
        return guides[k];
      }
    }
    return null;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function renderTable(guide) {
    let html = '<table><tr><th></th>';
    for (let i = 0; i < guide.measurements.length; i++) {
      html += '<th>' + escapeHtml(guide.measurements[i].name) + '</th>';
    }
    html += '</tr>';
    for (let i = 0; i < guide.sizes.length; i++) {
      html += '<tr><td><strong>' + escapeHtml(guide.sizes[i]) + '</strong></td>';
      for (let j = 0; j < guide.measurements.length; j++) {
        const v = guide.measurements[j].values[i] || '';
        html += '<td class="sgd-table-cm" data-cm="' + escapeHtml(v) + '">' + escapeHtml(v) + '</td>';
      }
      html += '</tr>';
    }
    html += '</table>';
    return html;
  }

  function renderHowToMeasure(guide) {
    if (!guide.imageUrl) return '';
    let html = '<div class="sgd-how-to-measure-text">How to measure </div>';
    html += '<div class="sgd-how-to-measure-img">';
    html += '<img src="' + escapeHtml(guide.imageUrl) + '" alt="" width="1000" height="auto" loading="lazy">';
    html += '</div>';
    html += '<div class="how-to-measure-descriptions">';
    for (let i = 0; i < guide.measurements.length; i++) {
      const m = guide.measurements[i];
      html += '<div class="how-to-measure-description">';
      html += '<div class="how-to-measure-description-title">' + (i + 1) + '. ' + escapeHtml(m.name) + '</div>';
      if (m.description) {
        html += '<div class="how-to-measure-description-text">' + escapeHtml(m.description) + '</div>';
      }
      html += '</div>';
    }
    html += '</div>';
    return html;
  }

  async function fillFallback(container) {
    if (container.dataset.sgdFilled) return;
    container.dataset.sgdFilled = '1';

    let guides;
    try {
      guides = await loadGuides();
    } catch (e) {
      console.warn('[size-guide-from-xlsx]', e);
      return;
    }
    if (!guides) return;

    const sku = container.dataset.productSku;
    const guide = findGuide(sku, guides);
    if (!guide) return;

    container.dataset.columns = String(guide.measurements.length);
    container.innerHTML = renderTable(guide);

    const root = container.closest('.size-guide-drawer-inner') || document;

    const howTo = root.querySelector('[data-sgd-how-to]');
    if (howTo && guide.imageUrl) {
      howTo.innerHTML = renderHowToMeasure(guide);
      howTo.removeAttribute('hidden');
    }

    const bodyTabs = root.querySelectorAll('.sgd-tab[data-target="body"]');
    const convTabs = root.querySelectorAll('.sgd-tab[data-target="measurements"]');
    bodyTabs.forEach(function (el) { el.classList.add('active'); });
    convTabs.forEach(function (el) { el.classList.remove('active'); });

    const bodyBtn = root.querySelector('[data-sgd-body-btn]');
    const convBtn = root.querySelector('.sdq-tab-btn[data-target="measurements"]');
    if (bodyBtn) {
      bodyBtn.removeAttribute('hidden');
      bodyBtn.classList.add('active');
    }
    if (convBtn) convBtn.classList.remove('active');

    const toggle = document.querySelector('input[name="size-guide-table-toggle-size-type"]');
    if (toggle) toggle.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // ---- Nuevo popup SIZE GUIDE (v2): render transpuesto (tallas en columnas) ----
  function renderV2Table(guide) {
    let html = '<table class="sgv2-table"><thead><tr><th></th>';
    for (let i = 0; i < guide.sizes.length; i++) {
      html += '<th data-sgv2-col="' + i + '">' + escapeHtml(guide.sizes[i]) + '</th>';
    }
    html += '</tr></thead><tbody>';
    for (let j = 0; j < guide.measurements.length; j++) {
      const m = guide.measurements[j];
      // La ultima fila se marca para cerrar por abajo el recuadro negro de la
      // columna seleccionada (antes dependia de :last-child).
      const lastRow = j === guide.measurements.length - 1 ? ' class="sgv2-lastrow"' : '';
      html += '<tr' + lastRow + '><td class="sgv2-mname">' + escapeHtml(m.name) + '</td>';
      for (let i = 0; i < guide.sizes.length; i++) {
        const v = m.values[i] || '';
        html += '<td class="sgv2-cell" data-sgv2-col="' + i + '" data-cm="' + escapeHtml(v) + '">' + escapeHtml(v) + '</td>';
      }
      html += '</tr>';
    }
    html += '</tbody></table>';
    return html;
  }

  function renderV2Squares(guide) {
    let html = '';
    for (let i = 0; i < guide.sizes.length; i++) {
      html += '<button type="button" class="sgv2-size" data-sgv2-size="' + i + '">' + escapeHtml(guide.sizes[i]) + '</button>';
    }
    return html;
  }

  function renderV2Howto(guide, title) {
    if (!guide.imageUrl && !(guide.measurements && guide.measurements.length)) return '';
    let html = '<div class="sgv2-howto-title">' + escapeHtml(title || 'GARMENT MEASUREMENTS') + '</div>';
    if (guide.imageUrl) {
      html += '<div class="sgv2-howto-img"><img src="' + escapeHtml(guide.imageUrl) + '" alt="" loading="lazy"></div>';
    }
    html += '<div class="sgv2-howto-list">';
    for (let i = 0; i < guide.measurements.length; i++) {
      const m = guide.measurements[i];
      html += '<div class="sgv2-howto-item"><div class="sgv2-howto-item-title">' + (i + 1) + '. ' + escapeHtml(m.name) + '</div>';
      if (m.description) html += '<div class="sgv2-howto-item-text">' + escapeHtml(m.description) + '</div>';
      html += '</div>';
    }
    html += '</div>';
    return html;
  }

  async function fillV2(drawer) {
    if (drawer.dataset.sgv2Filled) return;
    const fb = drawer.querySelector('[data-sgv2-fallback]');
    if (!fb) return; // producto con metafield: ya renderizado por Liquid
    drawer.dataset.sgv2Filled = '1';

    let guides;
    try { guides = await loadGuides(); } catch (e) { console.warn('[size-guide-from-xlsx] v2', e); return; }
    if (!guides) return;

    const sku = fb.dataset.productSku || drawer.dataset.productSku;
    const guide = findGuide(sku, guides);
    if (!guide) return;

    fb.innerHTML = renderV2Table(guide);

    const squares = drawer.querySelector('[data-sgv2-squares]');
    if (squares) {
      squares.innerHTML = renderV2Squares(guide);
      const firstSq = squares.querySelector('.sgv2-size');
      if (firstSq) firstSq.click();
    }

    const label = drawer.querySelector('[data-sgv2-selectlabel]');
    if (label) label.removeAttribute('hidden');

    const units = drawer.querySelector('[data-sgv2-units]');
    if (units) units.removeAttribute('hidden');

    const howTo = drawer.querySelector('[data-sgv2-howto]');
    if (howTo) {
      const h = renderV2Howto(guide, 'GARMENT MEASUREMENTS');
      if (h) { howTo.innerHTML = h; howTo.removeAttribute('hidden'); }
    }

    // ---- BODY MEASUREMENTS: solo si el Excel trae tabla de cuerpo para este SKU ----
    const bpanel = drawer.querySelector('[data-sgv2-panel="body"]');
    if (bpanel && guide.body && guide.body.measurements && guide.body.measurements.length) {
      const bfb = bpanel.querySelector('[data-sgv2-body-fallback]');
      if (bfb) bfb.innerHTML = renderV2Table(guide.body);
      const bsquares = bpanel.querySelector('[data-sgv2-body-squares]');
      if (bsquares) {
        bsquares.innerHTML = renderV2Squares(guide.body);
        const bf = bsquares.querySelector('.sgv2-size');
        if (bf) bf.click();
      }
      const blabel = bpanel.querySelector('.sgv2-select-label'); if (blabel) blabel.removeAttribute('hidden');
      const bunits = bpanel.querySelector('[data-sgv2-body-units]'); if (bunits) bunits.removeAttribute('hidden');
      const bhow = bpanel.querySelector('[data-sgv2-body-howto]');
      if (bhow) { const bh = renderV2Howto(guide.body, 'BODY MEASUREMENTS'); if (bh) { bhow.innerHTML = bh; bhow.removeAttribute('hidden'); } }
      const bempty = bpanel.querySelector('[data-sgv2-body-empty]'); if (bempty) bempty.setAttribute('hidden', '');
      // Revelar las pestañas y el tab BODY (ocultos por defecto)
      const tabs = drawer.querySelector('[data-sgv2-tabs]'); if (tabs) tabs.removeAttribute('hidden');
      const btab = drawer.querySelector('[data-sgv2-body-tab]'); if (btab) btab.removeAttribute('hidden');
    }
  }

  function initV2() {
    const drawer = document.querySelector('.size-guide-v2-drawer');
    if (!drawer) return;
    const obs = new MutationObserver(function (muts) {
      for (let i = 0; i < muts.length; i++) {
        if (muts[i].attributeName === 'class' && drawer.classList.contains('active')) {
          fillV2(drawer);
        }
      }
    });
    obs.observe(drawer, { attributes: true });
    if (drawer.classList.contains('active')) fillV2(drawer);
  }

  function init() {
    const containers = document.querySelectorAll('[data-sgd-fallback]');
    containers.forEach(function (container) {
      const drawer = container.closest('.size-guide-drawer');
      const details = container.closest('details');
      const trigger = function () { fillFallback(container); };

      if (drawer) {
        const obs = new MutationObserver(function (muts) {
          for (let i = 0; i < muts.length; i++) {
            if (muts[i].attributeName === 'class' && drawer.classList.contains('active')) {
              trigger();
            }
          }
        });
        obs.observe(drawer, { attributes: true });
        if (drawer.classList.contains('active')) trigger();
      }
      if (details) {
        details.addEventListener('toggle', function () {
          if (details.open) trigger();
        });
        if (details.open) trigger();
      }
    });
  }

  // ---- El disparador solo se muestra si el Excel trae medidas para este SKU ----
  // Los botones nacen con data-sg-pending y su fila esta oculta por CSS. Aqui se
  // resuelve: si hay guia se revela el boton (y el drawer queda ya relleno, asi
  // abre instantaneo); si no la hay se borra el boton y la fila queda vacia, que
  // el tema ya oculta con .product-info__block-item:empty.
  function dropTrigger(btn) {
    const row = btn.closest('.product-info__block-item') || btn.closest('.vp-size-guide-row');
    btn.remove();
    // Sin el :empty limpio, los saltos de linea de Liquid dejan la fila "no vacia"
    // y seguiria ocupando el gap del stack.
    if (row && !row.querySelector('*')) row.textContent = '';
  }

  async function resolveTriggers() {
    const triggers = document.querySelectorAll('.size-guide-open__button[data-sg-pending]');
    if (!triggers.length) return;

    const drawer = document.querySelector('.size-guide-v2-drawer');
    let guide = null;
    if (drawer) {
      const fb = drawer.querySelector('[data-sgv2-fallback]');
      const sku = (fb && fb.dataset.productSku) || drawer.dataset.productSku;
      try {
        const guides = await loadGuides();
        if (guides) guide = findGuide(sku, guides);
      } catch (e) {
        console.warn('[size-guide-from-xlsx] triggers', e);
      }
    }

    if (!guide) {
      triggers.forEach(dropTrigger);
      return;
    }

    await fillV2(drawer);
    triggers.forEach(function (btn) { btn.removeAttribute('data-sg-pending'); });
  }

  function whenIdle(fn) {
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 1);
  }

  // El tema re-renderiza product-info al cambiar de variante ("product:rerender"),
  // y eso devuelve el boton a su estado pendiente. Lo volvemos a resolver: es
  // barato porque loadGuides() ya tiene el Excel cacheado en dataPromise.
  function watchTriggers() {
    const host = document.querySelector('.product-info__block-list') || document.body;
    let queued = false;
    const obs = new MutationObserver(function () {
      if (queued) return;
      if (!document.querySelector('.size-guide-open__button[data-sg-pending]')) return;
      queued = true;
      whenIdle(function () { queued = false; resolveTriggers(); });
    });
    obs.observe(host, { childList: true, subtree: true });
  }

  function initAll() { init(); initV2(); whenIdle(resolveTriggers); watchTriggers(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
